import { parse as jidParse } from "./lib/cachedJid"
import { JID } from "@xmpp/jid"

export const driverJid = (chatJid: string) => {
  const jid = jidParse(chatJid)
  const vhost = extractVhostFromMuc(chatJid)

  return `${jid.getLocal()}@${vhost}`
}

/**
 * Produce the driver's room-form JID (`local@drivers.<vhost>`), the form the
 * fuel-report / driver-bot tables store. Inverse of `driverJid`, which strips
 * the `drivers.` subdomain down to the bare `local@<vhost>` form. Idempotent.
 */
export const driverRoomJid = (jid: string) => {
  const local = jidParse(jid).getLocal()
  const vhost = extractVhostFromJid(jid)
  return `${local}@drivers.${vhost}`
}

/**
 * Produce the driver's MUC room JID (`local@conference.<vhost>`) from either
 * the bare driver JID (`local@<vhost>`) or any room-form JID. Idempotent.
 */
export const driverConferenceJid = (jid: string) => {
  const local = jidParse(jid).getLocal()
  const vhost = extractVhostFromJid(jid)
  return `${local}@conference.${vhost}`
}

export const userJid = (chatJid: string) => {
  const jid = jidParse(chatJid)
  const vhost = extractVhostFromMuc(chatJid)

  const resource = jid.getResource()

  if (resource === "forwarder") {
    return `${jid.getLocal()}@${vhost}`
  }

  return `${resource}@${vhost}`
}

export const driverLocal = (driverJid: string) => {
  const jid = jidParse(driverJid)
  return jid.getLocal()
}

export const extractVhostFromMuc = (chatJid: string) => {
  const jid = jidParse(chatJid)
  const parts = jid.getDomain().split(".").slice(1)
  return parts.join(".")
}

export const extractVhostFromUser = (userJid: string) => {
  const jid = jidParse(userJid)
  return jid.getDomain()
}

export const extractVhostFromJid = (jid: string): string => {
  if (isMucJid(jid)) {
    return extractVhostFromMuc(jid)
  } else {
    return extractVhostFromUser(jid)
  }
}

/**
 * The identity behind a JID's forms: `local@vhost`, `local@drivers.vhost` and
 * `local@conference.vhost` all name the same driver, and a stored JID may use
 * any of them. Compare two JIDs through this, never as strings.
 */
export const jidIdentity = (jid: string): string =>
  `${jidParse(jid).getLocal()}@${extractVhostFromJid(jid)}`.toLowerCase()

export const isMucJid = (jid: string): boolean => {
  return (
    jid.includes("@conference.") ||
    jid.includes("@drivers.") ||
    jid.includes("@external.") ||
    jid.includes("@automation.")
  )
}

export const communicatorJid = (selfJid: JID) => {
  return `communicator@${selfJid.getDomain()}`
}

export const wizardJid = (selfJid: JID) => {
  return `wizard@${selfJid.getDomain()}`
}

export const getSlickshiftAIJid = (selfJid: JID) => {
  return `assistant@conference.${selfJid.getDomain()}`
}

export const getCMRJid = (selfJid: JID) => {
  return `cmr@automation.${selfJid.getDomain()}`
}

export const getCarIssuesJid = (selfJid: JID) => {
  return `car_issues@conference.${selfJid.getDomain()}`
}

export const getReceiptsJid = (selfJid: JID) => {
  return `receipts@conference.${selfJid.getDomain()}`
}

/**
 * Rooms the wizard forwards drivers' documents into (CMRs, receipts). A
 * forward there is the wizard relaying a driver's photo, so it is shown
 * under the driver's name rather than as a generic "Forwarded".
 */
export const isDocumentsRoomJid = (jid: string): boolean =>
  jid.startsWith("cmr@automation.") || jid.startsWith("receipts@conference.")
